import * as BABYLON from '@babylonjs/core';

/**
 * Modular Truss System v2 - Professional Event Rigging
 * Based on Global Truss specifications (F14-DT44P)
 * Uses Thin Instances for maximum browser performance
 * Uses TrussData.js for profile library & load calculations
 */

import { TRUSS_LIBRARY, CORNER_BLOCKS, BASE_PLATES, interpolateLoad, validateSpanLength, getLoadStatus, calculateSections, getAutoJunctionType } from './TrussData';

/**
 * Convert a TRUSS_LIBRARY profile to the internal render profile format.
 */
function getRendererProfile(profileId) {
  const lib = TRUSS_LIBRARY[profileId] || TRUSS_LIBRARY['F34'];
  return {
    label: lib.label,
    crossSection: lib.size_mm / 1000,
    mainTubeDiam: lib.mainTubeDiam_mm / 1000,
    mainTubeWall: lib.mainTubeWall_mm / 1000,
    braceDiam: (lib.braceDiam_mm || lib.mainTubeDiam_mm * 0.4) / 1000,
    braceWall: (lib.braceWall_mm || 2) / 1000,
    weightPerM: lib.weightPerMeter,
    chordCount: lib.chordCount,
    material: lib.material,
    bayInterval: lib.bayInterval || 0.5,
    geometry: lib.geometry,
  };
}

// Legacy alias for backward compatibility
const TRUSS_PROFILES = {
  get box() { return getRendererProfile('F34'); },
  get triangle() { return getRendererProfile('F33'); },
  get ladder() { return getRendererProfile('F32'); },
};

const BASE_PLATE_SIZE = 0.4;
const BASE_PLATE_HEIGHT = 0.015;
const OUTRIGGER_LENGTH = 0.8;
const OUTRIGGER_DIAM = 0.048;
const MOTOR_SIZE = 0.20;
const MOTOR_HEIGHT = 0.30;
const CONNECTOR_SIZE = 0.12;
const TESS_MAIN = 8;
const TESS_BRACE = 6;

/**
 * Get 2D chord positions in the cross-section plane (perpendicular to segment direction).
 * Returns {u, v} offsets where u = "right" axis and v = "up" axis of the cross-section.
 */
function getChordOffsets(profile) {
  const half = profile.crossSection / 2;
  if (profile.chordCount === 4) {
    return [
      { u: -half, v: -half },
      { u: half, v: -half },
      { u: half, v: half },
      { u: -half, v: half }
    ];
  } else if (profile.chordCount === 3) {
    const h = half * Math.sqrt(3);
    return [
      { u: -half, v: -h / 3 },
      { u: half, v: -h / 3 },
      { u: 0, v: 2 * h / 3 }
    ];
  } else {
    return [
      { u: -half, v: 0 },
      { u: half, v: 0 }
    ];
  }
}

/**
 * Create rotation quaternion to align a Babylon.js cylinder (Y-axis) from point A to point B
 */
function getAlignQuat(start, end) {
  const dir = end.subtract(start);
  const len = dir.length();
  if (len < 0.0001) return { quat: BABYLON.Quaternion.Identity(), length: 0 };
  const dirN = dir.normalize();
  const up = new BABYLON.Vector3(0, 1, 0);
  const dot = BABYLON.Vector3.Dot(up, dirN);

  if (dot > 0.9999) return { quat: BABYLON.Quaternion.Identity(), length: len };
  if (dot < -0.9999) return { quat: BABYLON.Quaternion.RotationAxis(BABYLON.Axis.X, Math.PI), length: len };

  const axis = BABYLON.Vector3.Cross(up, dirN).normalize();
  const angle = Math.acos(Math.max(-1, Math.min(1, dot)));
  return { quat: BABYLON.Quaternion.RotationAxis(axis, angle), length: len };
}

/**
 * Build a local coordinate system for a segment between two world-space points.
 * Returns { fwd, right, up } where fwd is along the segment, right and up define the cross-section plane.
 */
function buildLocalFrame(startPt, endPt) {
  const fwd = endPt.subtract(startPt);
  const length = fwd.length();
  if (length < 0.001) {
    return { fwd: BABYLON.Vector3.Up(), right: BABYLON.Vector3.Right(), up: BABYLON.Vector3.Forward(), length: 0 };
  }
  const fwdN = fwd.normalize();

  // Choose a reference "up" that is not parallel to fwd
  let refUp = new BABYLON.Vector3(0, 1, 0);
  if (Math.abs(BABYLON.Vector3.Dot(fwdN, refUp)) > 0.95) {
    refUp = new BABYLON.Vector3(0, 0, 1);
  }

  const right = BABYLON.Vector3.Cross(fwdN, refUp).normalize();
  const up = BABYLON.Vector3.Cross(right, fwdN).normalize();

  return { fwd: fwdN, right, up, length };
}

/**
 * Collect thin-instance matrices for a truss segment between two world-space points.
 * This is the core geometry builder - all structures use this.
 * 
 * @param {Object} profile - Truss profile
 * @param {BABYLON.Vector3} startPt - World-space start point (center of cross-section)
 * @param {BABYLON.Vector3} endPt - World-space end point (center of cross-section)
 * @param {Object} matrices - Output collectors
 */
function collectSegment(profile, startPt, endPt, matrices) {
  const { fwd, right, up, length } = buildLocalFrame(startPt, endPt);
  if (length < 0.001) return;

  const chords = getChordOffsets(profile);
  const numBays = Math.max(1, Math.round(length / profile.bayInterval));

  // Helper: get world-space point at fraction t along segment, offset by chord (u, v)
  const getPoint = (t, u, v) => {
    return startPt.add(fwd.scale(t * length))
      .add(right.scale(u))
      .add(up.scale(v));
  };

  // ===== Main chord tubes =====
  for (const ch of chords) {
    const cStart = getPoint(0, ch.u, ch.v);
    const cEnd = getPoint(1, ch.u, ch.v);
    const cMid = cStart.add(cEnd).scale(0.5);
    const { quat } = getAlignQuat(cStart, cEnd);
    matrices.mainTubes.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(1, length, 1), quat, cMid
    ));
  }

  // ===== Horizontal frames at each bay boundary + diagonal braces =====
  for (let bay = 0; bay <= numBays; bay++) {
    const t = bay / numBays;

    // Horizontal frame connecting adjacent chords at this bay position
    for (let i = 0; i < chords.length; i++) {
      const c1 = chords[i];
      const c2 = chords[(i + 1) % chords.length];
      const p1 = getPoint(t, c1.u, c1.v);
      const p2 = getPoint(t, c2.u, c2.v);
      const hMid = p1.add(p2).scale(0.5);
      const { quat, length: hLen } = getAlignQuat(p1, p2);
      if (hLen > 0.001) {
        matrices.horizontals.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, hLen, 1), quat, hMid
        ));
      }
    }

    // Diagonal braces on each face between this bay and next
    if (bay < numBays) {
      const t1 = bay / numBays;
      const t2 = (bay + 1) / numBays;

      for (let i = 0; i < chords.length; i++) {
        const c1 = chords[i];
        const c2 = chords[(i + 1) % chords.length];

        // V-brace pattern alternating per bay for classic truss look
        if (bay % 2 === 0) {
          const dA = getPoint(t1, c1.u, c1.v);
          const dB = getPoint(t2, c2.u, c2.v);
          addBrace(dA, dB, matrices);
          
          const dC = getPoint(t1, c2.u, c2.v);
          const dD = getPoint(t2, c1.u, c1.v);
          addBrace(dC, dD, matrices);
        } else {
          const dA = getPoint(t1, c2.u, c2.v);
          const dB = getPoint(t2, c1.u, c1.v);
          addBrace(dA, dB, matrices);
          
          const dC = getPoint(t1, c1.u, c1.v);
          const dD = getPoint(t2, c2.u, c2.v);
          addBrace(dC, dD, matrices);
        }
      }
    }
  }
}

function addBrace(p1, p2, matrices) {
  const mid = p1.add(p2).scale(0.5);
  const { quat, length } = getAlignQuat(p1, p2);
  if (length > 0.001) {
    matrices.braces.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(1, length, 1), quat, mid
    ));
  }
}

/**
 * Collect realistic joint hardware at a section joint.
 * Matches real Global Truss coupling system:
 * - Spigot: visible internal connector sleeve at joint seam
 * - Conical Pins: small cylinders through chord tube, protruding outward
 * - R-Clips: small wire rings at pin ends
 * Per chord: 1 spigot + 2 pins + 2 clips. 4-chord truss = 4 spigots + 8 pins + 8 clips.
 */
function collectJointHardware(profile, jointPos, dirN, matrices) {
  const { right, up } = buildLocalFrame(jointPos, jointPos.add(dirN));
  const chords = getChordOffsets(profile);
  const tubeDiam = profile.mainTubeDiam;
  const tubeRadius = tubeDiam / 2;

  // Spigot: visible connector sleeve at joint seam
  const spigotLength = tubeDiam * 2.5;

  // Pin: small but visible, protruding from chord surface
  const pinLength = tubeDiam * 1.1;
  const pinOffset = tubeDiam * 0.6;

  for (const ch of chords) {
    const chordPos = jointPos.add(right.scale(ch.u)).add(up.scale(ch.v));

    // Spigot: aligned with chord direction, centered at joint
    const spigotStart = chordPos.subtract(dirN.scale(spigotLength / 2));
    const spigotEnd = chordPos.add(dirN.scale(spigotLength / 2));
    const { quat: spigotQuat } = getAlignQuat(spigotStart, spigotEnd);
    matrices.jointBands.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(1, spigotLength, 1),
      spigotQuat,
      chordPos
    ));

    // Pin direction: perpendicular to chord tube, outward from truss body
    let pinDir;
    if (profile.chordCount === 2) {
      pinDir = ch.u > 0 ? right : right.scale(-1);
    } else {
      pinDir = ch.v >= 0 ? up : up.scale(-1);
    }

    // 2 pins per chord, offset along span direction
    for (const sign of [-1, 1]) {
      // Pin center offset outward from chord surface so it protrudes visibly
      const pinCenter = chordPos.add(dirN.scale(sign * pinOffset)).add(pinDir.scale(tubeRadius * 0.3));
      const pinStart = pinCenter.subtract(pinDir.scale(pinLength / 2));
      const pinEnd = pinCenter.add(pinDir.scale(pinLength / 2));
      const { quat: pinQuat } = getAlignQuat(pinStart, pinEnd);

      matrices.couplerPins.push(BABYLON.Matrix.Compose(
        new BABYLON.Vector3(1, pinLength, 1),
        pinQuat,
        pinCenter
      ));

      // R-Clip at protruding end of pin
      const clipPos = pinCenter.add(pinDir.scale(pinLength * 0.44));
      const yAxis = new BABYLON.Vector3(0, 1, 0);
      const clipDot = BABYLON.Vector3.Dot(yAxis, pinDir);
      let clipQuat;
      if (Math.abs(clipDot) > 0.9999) {
        clipQuat = BABYLON.Quaternion.Identity();
      } else {
        const clipAxis = BABYLON.Vector3.Cross(yAxis, pinDir).normalize();
        const clipAngle = Math.acos(Math.max(-1, Math.min(1, clipDot)));
        clipQuat = BABYLON.Quaternion.RotationAxis(clipAxis, clipAngle);
      }

      matrices.rClips.push(BABYLON.Matrix.Compose(
        BABYLON.Vector3.One(),
        clipQuat,
        clipPos
      ));
    }
  }
}

/**
 * Build a span as multiple truss sections with joint connectors between them.
 * @param {Object} profile - Renderer profile
 * @param {BABYLON.Vector3} startPt - Start point
 * @param {BABYLON.Vector3} endPt - End point  
 * @param {Object} matrices - Output collectors
 * @param {Object} config - Full truss config (for section override)
 * @param {string} [spanKey] - Which span this is (e.g., 'top', 'leftLeg', 'width') for userSections lookup
 */
function collectSectionedSpan(profile, startPt, endPt, matrices, config, spanKey) {
  const dir = endPt.subtract(startPt);
  const totalLen = dir.length();
  if (totalLen < 0.001) return;

  // Use per-span profile for section calculation
  const profileId = getSpanProfileId(config, spanKey);
  const userSections = config.sectionOverrides && config.sectionOverrides[spanKey];
  const sectionData = calculateSections(profileId, Math.round(totalLen * 100) / 100, userSections);
  const sections = sectionData.sections;

  if (!sections || sections.length === 0) {
    collectSegment(profile, startPt, endPt, matrices);
    return;
  }

  const dirN = dir.normalize();
  let offset = 0;

  for (let i = 0; i < sections.length; i++) {
    const secLen = sections[i];
    const secStart = startPt.add(dirN.scale(offset));
    const secEnd = startPt.add(dirN.scale(offset + secLen));
    collectSegment(profile, secStart, secEnd, matrices);

    // Add realistic joint hardware (pins + clips) between sections
    if (i < sections.length - 1) {
      collectJointHardware(profile, secEnd, dirN, matrices);
    }

    offset += secLen;
  }
}

/**
 * Add LED panel matrices on 4 sides of a box system top frame
 * (Kept as internal utility but no longer exposed in UI)
 */


/**
 * Render a proper box-frame corner block (like ST-UJB-F14).
 * Creates a cube frame made of tubes: 4 vertical tubes at corners,
 * 4 horizontal tubes at top, 4 horizontal tubes at bottom, with diagonals.
 * @param {BABYLON.Vector3} pos - world position (center of block)
 * @param {Object} matrices - collector
 * @param {number} [scale] - override size
 * @param {string} [junctionType] - junction type id
 * @param {Object} [profile] - truss profile for sizing
 */
function addConnector(pos, matrices, scale, junctionType, profile) {
  const cs = profile ? profile.crossSection : (scale || CONNECTOR_SIZE);
  const halfCS = cs * 0.5;
  const tubeDiam = cs * 0.08; // tube diameter for the corner block frame
  const jt = junctionType || 'straight';

  // 8 corners of the cube
  const corners = [
    new BABYLON.Vector3(-halfCS, -halfCS, -halfCS),
    new BABYLON.Vector3(halfCS, -halfCS, -halfCS),
    new BABYLON.Vector3(halfCS, -halfCS, halfCS),
    new BABYLON.Vector3(-halfCS, -halfCS, halfCS),
    new BABYLON.Vector3(-halfCS, halfCS, -halfCS),
    new BABYLON.Vector3(halfCS, halfCS, -halfCS),
    new BABYLON.Vector3(halfCS, halfCS, halfCS),
    new BABYLON.Vector3(-halfCS, halfCS, halfCS),
  ];

  // 12 edges of a cube: 4 bottom, 4 top, 4 vertical
  const edges = [
    [0,1],[1,2],[2,3],[3,0], // bottom
    [4,5],[5,6],[6,7],[7,4], // top
    [0,4],[1,5],[2,6],[3,7], // vertical
  ];

  // Add tubes for each edge of the cube
  for (const [a, b] of edges) {
    const start = pos.add(corners[a]);
    const end = pos.add(corners[b]);
    const mid = start.add(end).scale(0.5);
    const { quat, length: edgeLen } = getAlignQuat(start, end);
    if (edgeLen > 0.001) {
      matrices.connectors.push(BABYLON.Matrix.Compose(
        new BABYLON.Vector3(tubeDiam, edgeLen, tubeDiam),
        quat,
        mid
      ));
    }
  }

  // Add X-braces on front and back faces (diagonal bracing)
  const faceDiags = [
    [0, 5], [1, 4], // front face (z = -halfCS)
    [2, 7], [3, 6], // back face (z = +halfCS)
  ];
  for (const [a, b] of faceDiags) {
    const start = pos.add(corners[a]);
    const end = pos.add(corners[b]);
    const mid = start.add(end).scale(0.5);
    const { quat, length: dLen } = getAlignQuat(start, end);
    if (dLen > 0.001) {
      matrices.connectors.push(BABYLON.Matrix.Compose(
        new BABYLON.Vector3(tubeDiam * 0.7, dLen, tubeDiam * 0.7),
        quat,
        mid
      ));
    }
  }

  // Connection stubs extending outward from each face where truss connects
  const stubLen = cs * 0.3;
  const stubDiam = cs * 0.12;
  const stubDirs = [];

  if (jt === '2way') {
    stubDirs.push(new BABYLON.Vector3(0, -1, 0), new BABYLON.Vector3(1, 0, 0));
  } else if (jt === '3way') {
    stubDirs.push(new BABYLON.Vector3(1, 0, 0), new BABYLON.Vector3(-1, 0, 0), new BABYLON.Vector3(0, -1, 0));
  } else if (jt === '4way') {
    stubDirs.push(
      new BABYLON.Vector3(0, -1, 0), new BABYLON.Vector3(0, 1, 0),
      new BABYLON.Vector3(1, 0, 0), new BABYLON.Vector3(-1, 0, 0)
    );
  } else if (jt === 'tjunction') {
    stubDirs.push(new BABYLON.Vector3(1, 0, 0), new BABYLON.Vector3(0, 0, 1), new BABYLON.Vector3(0, 0, -1));
  }

  for (const dir of stubDirs) {
    const start = pos.add(dir.scale(halfCS));
    const end = pos.add(dir.scale(halfCS + stubLen));
    const mid = start.add(end).scale(0.5);
    const { quat, length: sLen } = getAlignQuat(start, end);
    if (sLen > 0.001) {
      matrices.junctionArms.push(BABYLON.Matrix.Compose(
        new BABYLON.Vector3(stubDiam, sLen, stubDiam),
        quat,
        mid
      ));
    }
  }
}

/**
 * Collect 4-side support legs at the base of each pillar.
 * Creates a pyramid-like base with 4 diagonal legs extending outward,
 * triangulated bracing between them, and small foot plates.
 * Auto-sized based on profile and structure height.
 */
function collectSupportLegs(positions, matrices, config) {
  const profileId = config.trussProfile || 'F34';
  const lib = TRUSS_LIBRARY[profileId];
  const cs = lib ? lib.size_mm / 1000 : 0.29;
  const legSpread = cs * 4; // How far legs extend outward
  const legHeight = cs * 3; // Height of the support leg triangle
  const tubeDiam = cs * 0.15; // Leg tube diameter
  const footSize = 0.12; // Foot plate size

  for (const pos of positions) {
    const baseCenter = new BABYLON.Vector3(pos.x, 0, pos.z);

    // 4 leg endpoints (spread outward at ground level)
    const legEnds = [
      new BABYLON.Vector3(pos.x + legSpread, 0, pos.z + legSpread),
      new BABYLON.Vector3(pos.x - legSpread, 0, pos.z + legSpread),
      new BABYLON.Vector3(pos.x - legSpread, 0, pos.z - legSpread),
      new BABYLON.Vector3(pos.x + legSpread, 0, pos.z - legSpread),
    ];

    // Hub point slightly above ground where legs meet the pillar
    const hubPoint = new BABYLON.Vector3(pos.x, legHeight, pos.z);

    // 4 diagonal legs from hub to foot
    for (const legEnd of legEnds) {
      const mid = hubPoint.add(legEnd).scale(0.5);
      const { quat, length: lLen } = getAlignQuat(hubPoint, legEnd);
      if (lLen > 0.001) {
        matrices.supportLegs.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(tubeDiam, lLen, tubeDiam),
          quat,
          mid
        ));
      }

      // Foot plate at the end
      matrices.basePlates.push(BABYLON.Matrix.Compose(
        new BABYLON.Vector3(footSize, 0.01, footSize),
        BABYLON.Quaternion.Identity(),
        new BABYLON.Vector3(legEnd.x, 0.005, legEnd.z)
      ));
    }

    // Horizontal bracing ring between legs (at 1/3 height)
    const braceY = legHeight * 0.33;
    const braceFrac = 0.67; // How far out the bracing is
    const bracePoints = [
      new BABYLON.Vector3(pos.x + legSpread * braceFrac, braceY, pos.z + legSpread * braceFrac),
      new BABYLON.Vector3(pos.x - legSpread * braceFrac, braceY, pos.z + legSpread * braceFrac),
      new BABYLON.Vector3(pos.x - legSpread * braceFrac, braceY, pos.z - legSpread * braceFrac),
      new BABYLON.Vector3(pos.x + legSpread * braceFrac, braceY, pos.z - legSpread * braceFrac),
    ];

    for (let i = 0; i < 4; i++) {
      const start = bracePoints[i];
      const end = bracePoints[(i + 1) % 4];
      const mid = start.add(end).scale(0.5);
      const { quat, length: bLen } = getAlignQuat(start, end);
      if (bLen > 0.001) {
        matrices.supportLegs.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(tubeDiam * 0.7, bLen, tubeDiam * 0.7),
          quat,
          mid
        ));
      }
    }

    // Small center plate at the hub
    matrices.basePlates.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(cs * 0.5, 0.01, cs * 0.5),
      BABYLON.Quaternion.Identity(),
      new BABYLON.Vector3(pos.x, legHeight, pos.z)
    ));
  }
}

/**
 * Collect base plates and outrigger legs at pillar bottom positions.
 * Uses selected base plate from catalog if available.
 */
function collectBasePlates(positions, matrices, config) {
  // Determine plate size from catalog or default
  const selectedBP = config && config.basePlateModel;
  let plateSize = BASE_PLATE_SIZE;
  let plateHeight = BASE_PLATE_HEIGHT;
  let showOutriggers = true;

  if (selectedBP) {
    // Import from TrussData at runtime to avoid circular dep
    const bpData = {
      'SQ-4137': { size: 0.6, thickness: 0.008, outriggers: false },
      'SQ-4137-BLK': { size: 0.6, thickness: 0.008, outriggers: false },
      'SQ-4141': { size: 0.6, thickness: 0.010, outriggers: true },
      'SQ-4145': { size: 0.8, thickness: 0.012, outriggers: true },
      'DT-4164': { size: 0.8, thickness: 0.012, outriggers: true },
      'SQ-4187': { size: 0.45, thickness: 0.006, outriggers: false },
      'SQ-4188': { size: 0.45, thickness: 0.008, outriggers: true },
      'SQ-4139': { size: 0.35, thickness: 0.006, outriggers: false },
      'F14-BP': { size: 0.25, thickness: 0.005, outriggers: false },
      'F32-BP': { size: 0.50, thickness: 0.008, outriggers: false },
    };
    const bp = bpData[selectedBP];
    if (bp) {
      plateSize = bp.size;
      plateHeight = bp.thickness;
      showOutriggers = bp.outriggers;
    }
  }

  for (const pos of positions) {
    // Base plate (flat on ground)
    matrices.basePlates.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(plateSize, plateHeight, plateSize),
      BABYLON.Quaternion.Identity(),
      new BABYLON.Vector3(pos.x, plateHeight / 2, pos.z)
    ));

    // Outrigger legs (4 directions)
    if (showOutriggers) {
      const dirs = [
        new BABYLON.Vector3(1, 0, 0),
        new BABYLON.Vector3(-1, 0, 0),
        new BABYLON.Vector3(0, 0, 1),
        new BABYLON.Vector3(0, 0, -1)
      ];
      for (const dir of dirs) {
        const start = new BABYLON.Vector3(pos.x, 0.03, pos.z);
        const end = start.add(dir.scale(OUTRIGGER_LENGTH));
        const mid = start.add(end).scale(0.5);
        const { quat, length: oLen } = getAlignQuat(start, end);
        matrices.outriggers.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, oLen, 1), quat, mid
        ));
        // Small foot plate at the end
        matrices.basePlates.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(0.15, 0.01, 0.15),
          BABYLON.Quaternion.Identity(),
          new BABYLON.Vector3(end.x, 0.005, end.z)
        ));
      }
    }
  }
}

/**
 * Collect motor blocks at pillar top positions
 */
function collectMotors(positions, topY, matrices) {
  for (const pos of positions) {
    // Motor housing: tall rectangular box sitting on top
    matrices.motors.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(MOTOR_SIZE * 0.8, MOTOR_HEIGHT * 1.5, MOTOR_SIZE * 0.8),
      BABYLON.Quaternion.Identity(),
      new BABYLON.Vector3(pos.x, topY + MOTOR_HEIGHT * 0.75, pos.z)
    ));
    // Chain/cable stub hanging below the motor
    matrices.motors.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(0.02, 0.3, 0.02),
      BABYLON.Quaternion.Identity(),
      new BABYLON.Vector3(pos.x, topY - 0.15, pos.z)
    ));
  }
}

// ===============================================================
// STRUCTURE BUILDERS (all use world-space start/end points)
// ===============================================================

/**
 * Resolve profile: supports both new library IDs (F34, DT44P) and old keys (box, triangle, ladder)
 */
function resolveProfile(config) {
  const profileKey = config.trussProfile || 'F34';
  // New-style: direct library ID
  if (TRUSS_LIBRARY[profileKey]) {
    return getRendererProfile(profileKey);
  }
  // Legacy mapping
  const legacyMap = { box: 'F34', triangle: 'F33', ladder: 'F32' };
  const mappedId = legacyMap[profileKey] || 'F34';
  return getRendererProfile(mappedId);
}

/**
 * Resolve profile for a specific span, falling back to main profile.
 * Also handles grid truss profiles stored in boxSystem.gridTrusses[].
 */
function resolveSpanProfile(config, spanKey) {
  // Grid truss profiles
  if (spanKey.startsWith('grid_')) {
    const idx = parseInt(spanKey.split('_')[1]);
    const gridTrusses = (config.boxSystem || {}).gridTrusses || [];
    if (gridTrusses[idx]?.profile && TRUSS_LIBRARY[gridTrusses[idx].profile]) {
      return getRendererProfile(gridTrusses[idx].profile);
    }
  }
  const sp = config.spanProfiles || {};
  if (sp[spanKey] && TRUSS_LIBRARY[sp[spanKey]]) {
    return getRendererProfile(sp[spanKey]);
  }
  return resolveProfile(config);
}

/**
 * Get the profile ID for a specific span.
 */
function getSpanProfileId(config, spanKey) {
  // Grid truss profiles
  if (spanKey.startsWith('grid_')) {
    const idx = parseInt(spanKey.split('_')[1]);
    const gridTrusses = (config.boxSystem || {}).gridTrusses || [];
    if (gridTrusses[idx]?.profile) return gridTrusses[idx].profile;
  }
  const sp = config.spanProfiles || {};
  return sp[spanKey] || config.trussProfile || 'F34';
}

/**
 * Route matrices — all spans use normal matrices.
 * Highlighting uses a separate wireframe bounding box, not separate meshes.
 */
function getSpanMatrices(matrices) {
  return matrices;
}


/**
 * Build a straight truss segment lying along the Z axis at ground level
 */
function buildStraight(config, matrices) {
  const profile = resolveSpanProfile(config, 'span');
  const length = config.length || 3;
  const cs = profile.crossSection / 2;

  const startPt = new BABYLON.Vector3(0, cs, -length / 2);
  const endPt = new BABYLON.Vector3(0, cs, length / 2);
  collectSectionedSpan(profile, startPt, endPt, getSpanMatrices(matrices, 'span'), config, 'span');

  const jt = getAutoJunctionType('straight');
  addConnector(startPt, matrices, null, jt, profile);
  addConnector(endPt, matrices, null, jt, profile);

  if (config.showBasePlates) {
    collectBasePlates([
      { x: 0, z: -length / 2 },
      { x: 0, z: length / 2 }
    ], matrices, config);
  }
}

/**
 * Build Goal Post: 2 vertical towers + 1 horizontal top truss
 * All pieces connect at the corners.
 */
function buildGoalPost(config, matrices) {
  const profile = resolveProfile(config);
  const gp = config.goalpost || {};
  const heightL = gp.heightLeft || 5;
  const heightR = gp.heightRight || 5;
  const topWidth = gp.topWidth || 6;
  const halfW = topWidth / 2;

  // Per-span profiles
  const leftLegProfile = resolveSpanProfile(config, 'leftLeg');
  const rightLegProfile = resolveSpanProfile(config, 'rightLeg');
  const topSpanProfile = resolveSpanProfile(config, 'topSpan');

  // Left vertical tower
  const leftBottom = new BABYLON.Vector3(-halfW, 0, 0);
  const leftTop = new BABYLON.Vector3(-halfW, heightL, 0);
  collectSectionedSpan(leftLegProfile, leftBottom, leftTop, getSpanMatrices(matrices, 'leftLeg'), config, 'leftLeg');

  // Right vertical tower
  const rightBottom = new BABYLON.Vector3(halfW, 0, 0);
  const rightTop = new BABYLON.Vector3(halfW, heightR, 0);
  collectSectionedSpan(rightLegProfile, rightBottom, rightTop, getSpanMatrices(matrices, 'rightLeg'), config, 'rightLeg');

  // Top horizontal truss
  const topY = Math.max(heightL, heightR);
  const horizStart = new BABYLON.Vector3(-halfW, topY, 0);
  const horizEnd = new BABYLON.Vector3(halfW, topY, 0);
  collectSectionedSpan(topSpanProfile, horizStart, horizEnd, getSpanMatrices(matrices, 'topSpan'), config, 'topSpan');

  // Auto junction: 2-way corners for goalpost
  const jt = getAutoJunctionType('goalpost');
  addConnector(new BABYLON.Vector3(-halfW, topY, 0), matrices, null, jt, profile);
  addConnector(new BABYLON.Vector3(halfW, topY, 0), matrices, null, jt, profile);

  if (config.showBasePlates !== false) {
    collectBasePlates([
      { x: -halfW, z: 0 },
      { x: halfW, z: 0 }
    ], matrices, config);
  }

  // 4-side support legs
  if (config.showSupportLegs !== false) {
    collectSupportLegs([
      { x: -halfW, z: 0 },
      { x: halfW, z: 0 }
    ], matrices, config);
  }
}

/**
 * Build Box System: vertical pillars + rectangular horizontal top frame
 */
function buildBoxSystem(config, matrices) {
  const profile = resolveProfile(config);
  const bs = config.boxSystem || {};
  const width = bs.width || 8;
  const depth = bs.depth || 6;
  const height = bs.height || 6;
  const halfW = width / 2;
  const halfD = depth / 2;
  const shiftX = bs.shiftX || 0;
  const liftH = bs.liftHeight || 0;
  const topY = height + liftH;

  // Per-span profiles
  const pillarProfile = resolveSpanProfile(config, 'pillar');
  const widthProfile = resolveSpanProfile(config, 'widthSpan');
  const depthProfile = resolveSpanProfile(config, 'depthSpan');

  // Determine pillar positions
  let pillarMode = bs.pillarMode || 'auto';
  let pillarCount = bs.pillarCount || 4;

  if (pillarMode === 'auto') {
    if (width > 20) pillarCount = 8;
    else if (width > 10) pillarCount = 6;
    else pillarCount = 4;
  }

  let pillarPositions;
  if (pillarCount === 4) {
    pillarPositions = [
      { x: -halfW + shiftX, z: -halfD },
      { x: halfW + shiftX, z: -halfD },
      { x: halfW + shiftX, z: halfD },
      { x: -halfW + shiftX, z: halfD }
    ];
  } else if (pillarCount === 6) {
    pillarPositions = [
      { x: -halfW + shiftX, z: -halfD },
      { x: shiftX, z: -halfD },
      { x: halfW + shiftX, z: -halfD },
      { x: halfW + shiftX, z: halfD },
      { x: shiftX, z: halfD },
      { x: -halfW + shiftX, z: halfD }
    ];
  } else {
    pillarPositions = [
      { x: -halfW + shiftX, z: -halfD },
      { x: shiftX, z: -halfD },
      { x: halfW + shiftX, z: -halfD },
      { x: halfW + shiftX, z: 0 },
      { x: halfW + shiftX, z: halfD },
      { x: shiftX, z: halfD },
      { x: -halfW + shiftX, z: halfD },
      { x: -halfW + shiftX, z: 0 }
    ];
  }

  // Build vertical pillars (sectioned)
  for (const pos of pillarPositions) {
    const bottom = new BABYLON.Vector3(pos.x, 0, pos.z);
    const top = new BABYLON.Vector3(pos.x, topY, pos.z);
    collectSectionedSpan(pillarProfile, bottom, top, getSpanMatrices(matrices, 'pillar'), config, 'pillar');
  }

  // Build top horizontal frame edges (sectioned)
  const fl = new BABYLON.Vector3(-halfW + shiftX, topY, -halfD);
  const fr = new BABYLON.Vector3(halfW + shiftX, topY, -halfD);
  collectSectionedSpan(widthProfile, fl, fr, getSpanMatrices(matrices, 'widthSpan'), config, 'widthSpan');

  const bl = new BABYLON.Vector3(-halfW + shiftX, topY, halfD);
  const br = new BABYLON.Vector3(halfW + shiftX, topY, halfD);
  collectSectionedSpan(widthProfile, bl, br, getSpanMatrices(matrices, 'widthSpan'), config, 'widthSpan');

  collectSectionedSpan(depthProfile, fl, bl, getSpanMatrices(matrices, 'depthSpan'), config, 'depthSpan');
  collectSectionedSpan(depthProfile, fr, br, getSpanMatrices(matrices, 'depthSpan'), config, 'depthSpan');

  // Auto junction: 4-way for box corners
  const jt = getAutoJunctionType('box_system');
  addConnector(fl, matrices, null, jt, profile);
  addConnector(fr, matrices, null, jt, profile);
  addConnector(bl, matrices, null, jt, profile);
  addConnector(br, matrices, null, jt, profile);

  // Top support grid
  if (bs.topSupport) {
    const cs = profile.crossSection;
    const gridY = topY + cs;
    const numCrossX = Math.max(1, Math.round(depth / 3));
    for (let i = 1; i <= numCrossX; i++) {
      const frac = i / (numCrossX + 1);
      const zPos = -halfD + depth * frac;
      collectSegment(profile,
        new BABYLON.Vector3(-halfW + shiftX, gridY, zPos),
        new BABYLON.Vector3(halfW + shiftX, gridY, zPos),
        matrices
      );
    }
    const numCrossZ = Math.max(1, Math.round(width / 3));
    for (let i = 1; i <= numCrossZ; i++) {
      const frac = i / (numCrossZ + 1);
      const xPos = -halfW + width * frac + shiftX;
      collectSegment(profile,
        new BABYLON.Vector3(xPos, gridY, -halfD),
        new BABYLON.Vector3(xPos, gridY, halfD),
        matrices
      );
    }
  }

  // ===== Grid Trusses (individual configurable trusses on top frame) =====
  const gridTrusses = bs.gridTrusses || [];
  if (gridTrusses.length > 0) {
    // Auto-detect frame truss cross-sections for proper Y stacking
    const widthFrameProfile = resolveSpanProfile(config, 'widthSpan');
    const depthFrameProfile = resolveSpanProfile(config, 'depthSpan');

    gridTrusses.forEach((gt, idx) => {
      const spanKey = `grid_${idx}`;
      const gtProfile = resolveSpanProfile(config, spanKey);
      const gtCS = gtProfile.crossSection;
      const attached = gt.attached !== false;
      const rotDeg = gt.rotation || 0;
      const rotRad = rotDeg * Math.PI / 180;
      const posZ = gt.position || 0;

      // Auto-detect which frame truss this grid truss crosses over
      // 0° = width direction → crosses depth spans
      // 90° = depth direction → crosses width spans
      // Other angles → use the larger cross-section for safety
      let crossingFrameCS;
      if (Math.abs(rotDeg % 180) < 5) {
        crossingFrameCS = depthFrameProfile.crossSection;
      } else if (Math.abs((rotDeg % 180) - 90) < 5) {
        crossingFrameCS = widthFrameProfile.crossSection;
      } else {
        crossingFrameCS = Math.max(widthFrameProfile.crossSection, depthFrameProfile.crossSection);
      }

      // Y position based on mode:
      // Attached = same level as frame (structurally integrated)
      // On Top = sits above the frame truss
      let y;
      if (attached) {
        y = topY;
      } else {
        y = topY + crossingFrameCS / 2 + gtCS / 2;
      }
      // Apply manual Y offset (shift up/down)
      y += (gt.offsetY != null ? gt.offsetY : 0);

      // Calculate length: default full span in the primary direction
      let defaultLen = width;
      if (Math.abs(rotDeg - 90) < 1 || Math.abs(rotDeg - 270) < 1) {
        defaultLen = depth;
      }
      const gtLen = gt.length || defaultLen;
      const halfLen = gtLen / 2;

      // Direction based on rotation (0 = width/X direction, 90 = depth/Z direction)
      const dirX = Math.cos(rotRad);
      const dirZ = Math.sin(rotRad);
      const centerX = shiftX + (gt.offsetX != null ? gt.offsetX : 0);
      const centerZ = posZ;

      const start = new BABYLON.Vector3(centerX - dirX * halfLen, y, centerZ - dirZ * halfLen);
      const end = new BABYLON.Vector3(centerX + dirX * halfLen, y, centerZ + dirZ * halfLen);

      collectSectionedSpan(gtProfile, start, end, getSpanMatrices(matrices, spanKey), config, spanKey);

      // If attached, add junction connectors at the endpoints
      if (attached) {
        const jt = getAutoJunctionType('box_system');
        addConnector(start, matrices, null, jt, gtProfile);
        addConnector(end, matrices, null, jt, gtProfile);
      }
    });
  }

  if (config.showBasePlates !== false) {
    collectBasePlates(pillarPositions, matrices, config);
  }

  if (bs.liftMode === 'motor') {
    collectMotors(pillarPositions, topY, matrices);
  }

  // 4-side support legs
  if (config.showSupportLegs !== false) {
    collectSupportLegs(pillarPositions, matrices, config);
  }
}

/**
 * Build Circular / Arc truss
 */
function buildCircular(config, matrices) {
  const profile = resolveProfile(config);
  const circ = config.circular || {};
  const radius = circ.radius || 3;
  const segments = Math.max(4, circ.segments || 12);
  const arcAngle = (circ.arcAngle || 360) * Math.PI / 180;
  const cs = profile.crossSection / 2;

  for (let i = 0; i < segments; i++) {
    const angle1 = i * arcAngle / segments;
    const angle2 = (i + 1) * arcAngle / segments;

    const startPt = new BABYLON.Vector3(
      radius * Math.cos(angle1), cs, radius * Math.sin(angle1)
    );
    const endPt = new BABYLON.Vector3(
      radius * Math.cos(angle2), cs, radius * Math.sin(angle2)
    );

    collectSegment(profile, startPt, endPt, matrices);

    const jt = getAutoJunctionType('circular');
    addConnector(endPt, matrices, null, jt, profile);
  }
}

function buildAngle(config, matrices) {
  const profile = resolveProfile(config);
  const ang = config.angle || {};
  const length1 = ang.length1 || 2;
  const length2 = ang.length2 || 2;
  const angleDeg = ang.angle || 90;
  const angleRad = angleDeg * Math.PI / 180;
  const direction = ang.direction || 'horizontal';
  const cs = profile.crossSection / 2;

  const arm1Profile = resolveSpanProfile(config, 'arm1');
  const arm2Profile = resolveSpanProfile(config, 'arm2');

  const joint = new BABYLON.Vector3(0, cs, 0);

  if (direction === 'horizontal') {
    const seg1Start = joint.add(new BABYLON.Vector3(0, 0, -length1));
    collectSectionedSpan(arm1Profile, seg1Start, joint, getSpanMatrices(matrices, 'arm1'), config, 'arm1');

    const seg2End = joint.add(new BABYLON.Vector3(
      Math.sin(angleRad) * length2, 0, Math.cos(angleRad) * length2
    ));
    collectSectionedSpan(arm2Profile, joint, seg2End, getSpanMatrices(matrices, 'arm2'), config, 'arm2');
  } else {
    const seg1Start = joint.add(new BABYLON.Vector3(0, 0, -length1));
    collectSectionedSpan(arm1Profile, seg1Start, joint, getSpanMatrices(matrices, 'arm1'), config, 'arm1');

    const seg2End = joint.add(new BABYLON.Vector3(
      0, Math.sin(angleRad) * length2, Math.cos(angleRad) * length2
    ));
    collectSectionedSpan(arm2Profile, joint, seg2End, getSpanMatrices(matrices, 'arm2'), config, 'arm2');
  }

  const jt = getAutoJunctionType('angle');
  addConnector(joint, matrices, null, jt, profile);
}

// ===============================================================
// LOAD CALCULATION
// ===============================================================

export function calculateTrussLoad(config) {
  const profile = resolveProfile(config);
  const profileId = config.trussProfile || 'F34';
  const libProfile = TRUSS_LIBRARY[profileId] || TRUSS_LIBRARY['F34'];
  const structureType = config.structureType || 'straight';

  let totalLength = 0;
  let mainSpan = 0; // The critical span for load lookup

  switch (structureType) {
    case 'straight':
      totalLength = config.length || 3;
      mainSpan = totalLength;
      break;
    case 'goalpost': {
      const gp = config.goalpost || {};
      const topW = gp.topWidth || 6;
      totalLength = (gp.heightLeft || 5) + (gp.heightRight || 5) + topW;
      mainSpan = topW; // Critical span is the horizontal top
      break;
    }
    case 'box_system': {
      const bs = config.boxSystem || {};
      const w = bs.width || 8;
      const d = bs.depth || 6;
      const h = bs.height || 6;
      let pc = bs.pillarCount || 4;
      if ((bs.pillarMode || 'auto') === 'auto') {
        if (w > 20) pc = 8;
        else if (w > 10) pc = 6;
        else pc = 4;
      }
      totalLength = pc * h + 2 * (w + d);
      mainSpan = Math.max(w, d); // Critical span is the longest horizontal
      break;
    }
    case 'circular': {
      const circ = config.circular || {};
      const r = circ.radius || 3;
      const arcDeg = circ.arcAngle || 360;
      totalLength = 2 * Math.PI * r * (arcDeg / 360);
      mainSpan = totalLength;
      break;
    }
    case 'angle': {
      const ang = config.angle || {};
      totalLength = (ang.length1 || 2) + (ang.length2 || 2);
      mainSpan = Math.max(ang.length1 || 2, ang.length2 || 2);
      break;
    }
    default:
      totalLength = config.length || 3;
      mainSpan = totalLength;
  }

  const totalWeight = totalLength * profile.weightPerM;
  const loadData = interpolateLoad(libProfile.id, mainSpan);
  const spanValidation = validateSpanLength(libProfile.id, mainSpan);

  // Applied load (user-specified)
  const appliedLoad = config.appliedLoad || 0;
  const maxSafe = loadData ? loadData.pointLoad : 0;
  const loadStatus = getLoadStatus(appliedLoad, maxSafe);

  // Section breakdowns for each span
  const sectionBreakdowns = {};
  let totalJoints = 0;
  let totalSections = 0;
  const sectionCounts = {}; // e.g., { '2m': 4, '3m': 2 }

  const addSpanBreakdown = (key, spanLen) => {
    // Use per-span profile for section calculation
    const spanProfId = getSpanProfileId(config, key);
    const secData = calculateSections(spanProfId, Math.round(spanLen * 100) / 100, config.sectionOverrides && config.sectionOverrides[key]);
    sectionBreakdowns[key] = { length: spanLen, profileId: spanProfId, ...secData };
    totalJoints += secData.jointCount;
    totalSections += secData.sections.length;
    secData.sections.forEach(s => {
      const k = `${s}m`;
      sectionCounts[k] = (sectionCounts[k] || 0) + 1;
    });
  };

  switch (structureType) {
    case 'straight':
      addSpanBreakdown('span', config.length || 3);
      break;
    case 'goalpost': {
      const gp2 = config.goalpost || {};
      addSpanBreakdown('leftLeg', gp2.heightLeft || 5);
      addSpanBreakdown('rightLeg', gp2.heightRight || 5);
      addSpanBreakdown('topSpan', gp2.topWidth || 6);
      break;
    }
    case 'box_system': {
      const bs2 = config.boxSystem || {};
      let pc2 = bs2.pillarCount || 4;
      if ((bs2.pillarMode || 'auto') === 'auto') {
        if ((bs2.width || 8) > 20) pc2 = 8;
        else if ((bs2.width || 8) > 10) pc2 = 6;
        else pc2 = 4;
      }
      addSpanBreakdown('pillar', bs2.height || 6);
      // Multiply pillar sections by count
      const pillarSec = sectionBreakdowns['pillar'];
      totalJoints += pillarSec.jointCount * (pc2 - 1);
      totalSections += pillarSec.sections.length * (pc2 - 1);
      pillarSec.sections.forEach(s => {
        const k = `${s}m`;
        sectionCounts[k] = (sectionCounts[k] || 0) + (pc2 - 1);
      });
      addSpanBreakdown('widthSpan', bs2.width || 8);
      // x2 for front+back
      const wSec = sectionBreakdowns['widthSpan'];
      totalJoints += wSec.jointCount;
      totalSections += wSec.sections.length;
      wSec.sections.forEach(s => {
        const k = `${s}m`;
        sectionCounts[k] = (sectionCounts[k] || 0) + 1;
      });
      addSpanBreakdown('depthSpan', bs2.depth || 6);
      // x2 for left+right
      const dSec = sectionBreakdowns['depthSpan'];
      totalJoints += dSec.jointCount;
      totalSections += dSec.sections.length;
      dSec.sections.forEach(s => {
        const k = `${s}m`;
        sectionCounts[k] = (sectionCounts[k] || 0) + 1;
      });
      // Grid trusses on top frame
      const gridTrusses2 = bs2.gridTrusses || [];
      gridTrusses2.forEach((gt, idx) => {
        const rotDeg = gt.rotation || 0;
        let defaultLen = bs2.width || 8;
        if (Math.abs(rotDeg - 90) < 1 || Math.abs(rotDeg - 270) < 1) {
          defaultLen = bs2.depth || 6;
        }
        const gtLen = gt.length || defaultLen;
        addSpanBreakdown(`grid_${idx}`, gtLen);
      });
      break;
    }
    case 'angle': {
      const ang2 = config.angle || {};
      addSpanBreakdown('arm1', ang2.length1 || 2);
      addSpanBreakdown('arm2', ang2.length2 || 2);
      break;
    }
    default:
      addSpanBreakdown('span', config.length || 3);
  }

  // Corner block count
  let cornerBlockCount = 0;
  switch (structureType) {
    case 'goalpost': cornerBlockCount = 2; break;
    case 'box_system': cornerBlockCount = 4; break;
    case 'angle': cornerBlockCount = 1; break;
    default: cornerBlockCount = 0;
  }

  return {
    profile: libProfile.id,
    profileLabel: libProfile.label,
    geometry: libProfile.geometry,
    structureType,
    totalLength: Math.round(totalLength * 100) / 100,
    mainSpan: Math.round(mainSpan * 100) / 100,
    totalWeight: Math.round(totalWeight * 100) / 100,
    weightPerM: profile.weightPerM,
    material: profile.material,
    size_mm: libProfile.size_mm,
    // Load data from table
    maxUDL: loadData ? loadData.udl : 0,
    maxPointLoad: loadData ? loadData.pointLoad : 0,
    deflection: loadData ? loadData.deflection : 0,
    loadInterpolated: loadData ? loadData.interpolated : false,
    exceedsMaxSpan: loadData ? loadData.exceedsMax : true,
    // Span validation
    spanValid: spanValidation.valid,
    spanExact: spanValidation.exact,
    spanMessage: spanValidation.message,
    spanCombinations: spanValidation.combinations || [],
    closestSmaller: spanValidation.closestSmaller,
    closestLarger: spanValidation.closestLarger,
    // Load status
    appliedLoad,
    loadRatio: loadStatus.ratio,
    loadStatusText: loadStatus.status,
    loadStatusColor: loadStatus.color,
    // Section breakdowns
    sectionBreakdowns,
    sectionCounts,
    totalSections,
    totalJoints,
    cornerBlockCount,
    // Hardware BOM (Bill of Materials)
    totalCouplerPins: totalJoints * 8,
    totalSpacers: totalJoints * 4,
    totalClips: totalJoints * 8,
  };
}

// ===============================================================
// MAIN BUILD FUNCTION
// ===============================================================

export function buildTruss(config, objId, scene) {
  const profile = resolveProfile(config);
  const structColor = config.color || '#C0C0C0';

  const root = new BABYLON.TransformNode('trussRoot_' + objId, scene);
  root.metadata = { id: objId, type: 'object', isTruss: true };

  const col = BABYLON.Color3.FromHexString(structColor);

  // PBR materials
  const mainMat = new BABYLON.PBRMaterial('trussMat_' + objId, scene);
  mainMat.albedoColor = col;
  mainMat.metallic = 0.85;
  mainMat.roughness = 0.25;
  mainMat.backFaceCulling = false;

  const connMat = new BABYLON.PBRMaterial('trussConnMat_' + objId, scene);
  connMat.albedoColor = new BABYLON.Color3(0.35, 0.35, 0.4); // Darker steel for corner block frame
  connMat.metallic = 0.95;
  connMat.roughness = 0.1;
  connMat.backFaceCulling = false;

  const motorMat = new BABYLON.PBRMaterial('trussMotorMat_' + objId, scene);
  motorMat.albedoColor = new BABYLON.Color3(0.15, 0.15, 0.15);
  motorMat.metallic = 0.3;
  motorMat.roughness = 0.6;
  motorMat.backFaceCulling = false;

  // ===== Collect all matrices =====
  const matrices = {
    mainTubes: [],
    braces: [],
    horizontals: [],
    connectors: [],
    junctionArms: [],
    couplerPins: [],
    rClips: [],
    jointBands: [],
    basePlates: [],
    outriggers: [],
    motors: [],
    supportLegs: [],
  };

  // Highlighted span support: separate matrix arrays for the selected span
  if (config.highlightedSpan) {
    matrices._highlightedSpan = config.highlightedSpan;
    matrices._highlighted = {
      mainTubes: [],
      braces: [],
      horizontals: [],
      couplerPins: [],
      rClips: [],
      jointBands: [],
    };
  }

  const structureType = config.structureType || 'straight';

  switch (structureType) {
    case 'straight': buildStraight(config, matrices); break;
    case 'goalpost': buildGoalPost(config, matrices); break;
    case 'box_system': buildBoxSystem(config, matrices); break;
    case 'circular': buildCircular(config, matrices); break;
    case 'angle': buildAngle(config, matrices); break;
    default: buildStraight(config, matrices);
  }

  // ===== Create master meshes with thin instances =====

  if (matrices.mainTubes.length > 0) {
    const m = BABYLON.MeshBuilder.CreateCylinder('trussMC_' + objId, {
      height: 1.0, diameter: profile.mainTubeDiam, tessellation: TESS_MAIN
    }, scene);
    m.material = mainMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.mainTubes);
  }

  if (matrices.braces.length > 0) {
    const m = BABYLON.MeshBuilder.CreateCylinder('trussBR_' + objId, {
      height: 1.0, diameter: profile.braceDiam, tessellation: TESS_BRACE
    }, scene);
    m.material = mainMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.braces);
  }

  if (matrices.horizontals.length > 0) {
    const m = BABYLON.MeshBuilder.CreateCylinder('trussHZ_' + objId, {
      height: 1.0, diameter: profile.braceDiam, tessellation: TESS_BRACE
    }, scene);
    m.material = mainMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.horizontals);
  }

  if (matrices.connectors.length > 0) {
    const m = BABYLON.MeshBuilder.CreateBox('trussCN_' + objId, { size: 1.0 }, scene);
    m.material = connMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.connectors);
  }

  if (matrices.junctionArms.length > 0) {
    const m = BABYLON.MeshBuilder.CreateBox('trussJA_' + objId, { size: 1.0 }, scene);
    m.material = connMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.junctionArms);
  }

  if (matrices.basePlates.length > 0) {
    const m = BABYLON.MeshBuilder.CreateBox('trussBP_' + objId, { size: 1.0 }, scene);
    m.material = connMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.basePlates);
  }

  if (matrices.outriggers.length > 0) {
    const m = BABYLON.MeshBuilder.CreateCylinder('trussOL_' + objId, {
      height: 1.0, diameter: OUTRIGGER_DIAM, tessellation: TESS_BRACE
    }, scene);
    m.material = mainMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.outriggers);
  }

  if (matrices.motors.length > 0) {
    const m = BABYLON.MeshBuilder.CreateBox('trussMotor_' + objId, { size: 1.0 }, scene);
    m.material = motorMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.motors);
  }

  // Coupler Pins (small visible pins through chord tubes at joints)
  if (matrices.couplerPins.length > 0) {
    const pinMat = new BABYLON.PBRMaterial('trussPinMat_' + objId, scene);
    pinMat.albedoColor = new BABYLON.Color3(0.85, 0.85, 0.88);
    pinMat.metallic = 0.95;
    pinMat.roughness = 0.08;
    pinMat.backFaceCulling = false;
    const pinDiam = profile.mainTubeDiam * 0.14;
    const m = BABYLON.MeshBuilder.CreateCylinder('trussPin_' + objId, {
      height: 1.0, diameter: pinDiam, tessellation: 8
    }, scene);
    m.material = pinMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.couplerPins);
  }

  // R-Clips (small wire rings at pin ends - visible but proportionate)
  if (matrices.rClips.length > 0) {
    const clipMat = new BABYLON.PBRMaterial('trussClipMat_' + objId, scene);
    clipMat.albedoColor = new BABYLON.Color3(0.55, 0.55, 0.58);
    clipMat.metallic = 0.85;
    clipMat.roughness = 0.25;
    clipMat.backFaceCulling = false;
    const clipDiam = profile.mainTubeDiam * 0.28;
    const clipThick = profile.mainTubeDiam * 0.055;
    const m = BABYLON.MeshBuilder.CreateTorus('trussClip_' + objId, {
      diameter: clipDiam, thickness: clipThick, tessellation: 10
    }, scene);
    m.material = clipMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.rClips);
  }

  // Spigot connectors (visible coupling sleeves at joint seams)
  if (matrices.jointBands.length > 0) {
    const spigotMat = new BABYLON.PBRMaterial('trussSpigotMat_' + objId, scene);
    spigotMat.albedoColor = new BABYLON.Color3(0.50, 0.50, 0.53);
    spigotMat.metallic = 0.90;
    spigotMat.roughness = 0.22;
    spigotMat.backFaceCulling = false;
    const spigotDiam = profile.mainTubeDiam * 1.15;
    const m = BABYLON.MeshBuilder.CreateCylinder('trussSpigot_' + objId, {
      height: 1.0, diameter: spigotDiam, tessellation: TESS_MAIN
    }, scene);
    m.material = spigotMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.jointBands);
  }

  // 4-side support legs
  if (matrices.supportLegs.length > 0) {
    const legMat = new BABYLON.PBRMaterial('trussSupportMat_' + objId, scene);
    legMat.albedoColor = new BABYLON.Color3(0.6, 0.6, 0.6);
    legMat.metallic = 0.9;
    legMat.roughness = 0.2;
    legMat.backFaceCulling = false;
    const m = BABYLON.MeshBuilder.CreateBox('trussSL_' + objId, { size: 1.0 }, scene);
    m.material = legMat;
    m.parent = root;
    m.metadata = { trussMaster: true };
    applyThinInstances(m, matrices.supportLegs);
  }

  // ===== Highlighted span outline (thin yellow wireframe bounding box) =====
  if (matrices._highlighted) {
    const hl = matrices._highlighted;
    // Collect all positions from highlighted span matrices to compute bounding box
    const allMats = [...hl.mainTubes, ...hl.braces, ...hl.horizontals, ...hl.couplerPins, ...hl.rClips, ...hl.jointBands];
    if (allMats.length > 0) {
      let minX = Infinity, minY = Infinity, minZ = Infinity;
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      const pad = profile.crossSection * 0.6; // padding around the geometry
      for (let i = 0; i < allMats.length; i++) {
        const m = allMats[i];
        // Extract position from 4x4 matrix (indices 12,13,14)
        const px = m[12], py = m[13], pz = m[14];
        // Extract scale approximation from matrix columns
        const sx = Math.sqrt(m[0]*m[0] + m[1]*m[1] + m[2]*m[2]);
        const sy = Math.sqrt(m[4]*m[4] + m[5]*m[5] + m[6]*m[6]);
        const sz = Math.sqrt(m[8]*m[8] + m[9]*m[9] + m[10]*m[10]);
        const ext = Math.max(sx, sy, sz) * 0.5;
        if (px - ext < minX) minX = px - ext;
        if (py - ext < minY) minY = py - ext;
        if (pz - ext < minZ) minZ = pz - ext;
        if (px + ext > maxX) maxX = px + ext;
        if (py + ext > maxY) maxY = py + ext;
        if (pz + ext > maxZ) maxZ = pz + ext;
      }
      // Add padding
      minX -= pad; minY -= pad; minZ -= pad;
      maxX += pad; maxY += pad; maxZ += pad;
      // 8 corners of the bounding box
      const c = [
        new BABYLON.Vector3(minX, minY, minZ), // 0: front-bottom-left
        new BABYLON.Vector3(maxX, minY, minZ), // 1: front-bottom-right
        new BABYLON.Vector3(maxX, maxY, minZ), // 2: front-top-right
        new BABYLON.Vector3(minX, maxY, minZ), // 3: front-top-left
        new BABYLON.Vector3(minX, minY, maxZ), // 4: back-bottom-left
        new BABYLON.Vector3(maxX, minY, maxZ), // 5: back-bottom-right
        new BABYLON.Vector3(maxX, maxY, maxZ), // 6: back-top-right
        new BABYLON.Vector3(minX, maxY, maxZ), // 7: back-top-left
      ];
      // 12 edges of the box
      const lines = [
        [c[0], c[1]], [c[1], c[2]], [c[2], c[3]], [c[3], c[0]], // front face
        [c[4], c[5]], [c[5], c[6]], [c[6], c[7]], [c[7], c[4]], // back face
        [c[0], c[4]], [c[1], c[5]], [c[2], c[6]], [c[3], c[7]], // connecting edges
      ];
      const yellowColor = new BABYLON.Color4(1, 1, 0, 1);
      const colors = lines.map(seg => [yellowColor, yellowColor]);
      const linesMesh = BABYLON.MeshBuilder.CreateLineSystem('trussHLBox_' + objId, {
        lines, colors
      }, scene);
      linesMesh.parent = root;
      linesMesh.isPickable = false;
    }
  }

  // ===== Bounding box for picking =====
  const bounds = computeBounds(config);
  const bm = BABYLON.MeshBuilder.CreateBox('trussBound_' + objId, {
    width: bounds.w, height: bounds.h, depth: bounds.d
  }, scene);
  bm.position = new BABYLON.Vector3(bounds.cx, bounds.cy, bounds.cz);
  bm.visibility = 0;
  bm.isPickable = true;
  bm.metadata = { id: objId, type: 'object', trussHelper: true };
  bm.parent = root;

  root.isPickable = true;
  return root;
}

function computeBounds(config) {
  const profile = resolveProfile(config);
  const cs = profile.crossSection;
  const st = config.structureType || 'straight';

  switch (st) {
    case 'straight': {
      const len = config.length || 3;
      return { w: cs + 0.1, h: cs + 0.1, d: len + 0.1, cx: 0, cy: cs / 2, cz: 0 };
    }
    case 'goalpost': {
      const gp = config.goalpost || {};
      const h = Math.max(gp.heightLeft || 5, gp.heightRight || 5);
      const w = (gp.topWidth || 6) + cs;
      return { w, h: h + cs, d: cs + 1, cx: 0, cy: h / 2, cz: 0 };
    }
    case 'box_system': {
      const bs = config.boxSystem || {};
      const w = (bs.width || 8) + cs;
      const d = (bs.depth || 6) + cs;
      const h = (bs.height || 6) + (bs.liftHeight || 0) + cs;
      return { w, h, d, cx: bs.shiftX || 0, cy: h / 2, cz: 0 };
    }
    case 'circular': {
      const circ = config.circular || {};
      const r = (circ.radius || 3) + cs;
      return { w: r * 2 + 0.1, h: cs + 0.1, d: r * 2 + 0.1, cx: 0, cy: cs / 2, cz: 0 };
    }
    case 'angle': {
      const ang = config.angle || {};
      const len = Math.max(ang.length1 || 2, ang.length2 || 2);
      return { w: len + cs, h: len + cs, d: len + cs, cx: 0, cy: len / 2, cz: 0 };
    }
    default:
      return { w: 1, h: 1, d: 1, cx: 0, cy: 0.5, cz: 0 };
  }
}

function applyThinInstances(master, matrices) {
  if (matrices.length === 0) return;
  const buf = new Float32Array(16 * matrices.length);
  for (let i = 0; i < matrices.length; i++) {
    matrices[i].copyToArray(buf, i * 16);
  }
  master.thinInstanceSetBuffer('matrix', buf, 16);
  master.thinInstanceCount = matrices.length;
  master.isPickable = false;
}

export function hasTrussConfigChanged(prev, next) {
  if (!prev || !next) return true;
  return (
    prev.structureType !== next.structureType ||
    prev.trussProfile !== next.trussProfile ||
    JSON.stringify(prev.spanProfiles) !== JSON.stringify(next.spanProfiles) ||
    JSON.stringify(prev.boxSystem?.gridTrusses) !== JSON.stringify(next.boxSystem?.gridTrusses) ||
    prev.highlightedSpan !== next.highlightedSpan ||
    prev.color !== next.color ||
    prev.length !== next.length ||
    prev.showBasePlates !== next.showBasePlates ||
    prev.showSupportLegs !== next.showSupportLegs ||
    prev.junctionType !== next.junctionType ||
    prev.basePlateModel !== next.basePlateModel ||
    prev.cornerBlockModel !== next.cornerBlockModel ||
    JSON.stringify(prev.sectionOverrides || {}) !== JSON.stringify(next.sectionOverrides || {}) ||
    JSON.stringify(prev.goalpost || {}) !== JSON.stringify(next.goalpost || {}) ||
    JSON.stringify(prev.boxSystem || {}) !== JSON.stringify(next.boxSystem || {}) ||
    JSON.stringify(prev.circular || {}) !== JSON.stringify(next.circular || {}) ||
    JSON.stringify(prev.angle || {}) !== JSON.stringify(next.angle || {})
  );
}

export { TRUSS_PROFILES, TRUSS_LIBRARY, getRendererProfile, CORNER_BLOCKS, BASE_PLATES };
